import { useState } from 'react';
import { Link } from 'react-router-dom';
import { warehouseListings, warehouseCities, warehouseCategories } from '@/mocks/warehouseData';
import SupplierAuthModal from './components/SupplierAuthModal';
import ProductUploadModal from './components/ProductUploadModal';
import OrderModal from './components/OrderModal';
import SEOHead from '@/components/feature/SEOHead';

const WAREHOUSE_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '富缘佳旺本地仓储直邮 - 全球800城市华人好物',
    description: '富缘佳旺本地仓储直邮平台，精选优质服装、婴儿用品、书籍文具，供应商本地发货，快速送达。平台仅收取2%佣金。',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/warehouse`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
        { '@type': 'ListItem', position: 2, name: '本地仓储直邮', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/warehouse` },
      ],
    },
  },
];

type Listing = typeof warehouseListings[0];

export default function WarehousePage() {
  const [showAuth, setShowAuth] = useState(false);
  const [supplier, setSupplier] = useState<{ name: string; city: string; contact: string } | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [orderProduct, setOrderProduct] = useState<Listing | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeCity, setActiveCity] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = warehouseListings.filter((p) => {
    const catMatch = activeCategory === 'all' || p.category === activeCategory;
    const cityMatch = activeCity === 'all' || p.warehouseCity === activeCity;
    const searchMatch = !searchQuery || p.name.includes(searchQuery) || p.nameEn.toLowerCase().includes(searchQuery.toLowerCase());
    return catMatch && cityMatch && searchMatch;
  });

  const handleSupplierSuccess = (sup: { name: string; city: string; contact: string }) => {
    setSupplier(sup);
    setShowAuth(false);
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    setUploadSuccess(true);
    setTimeout(() => setUploadSuccess(false), 4000);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <SEOHead
        title="本地仓储直邮 - 富缘佳旺全球华人好物购物平台 | FortuneMatch"
        description="富缘佳旺本地仓储直邮，精选优质服装、婴儿用品、书籍文具。供应商本地发货，全球800城市覆盖，平台仅收2%佣金，快速送达。"
        keywords="华人购物,本地仓储,直邮到家,富缘佳旺商城,海外华人购物"
        canonical="/warehouse"
        jsonLd={WAREHOUSE_JSONLD}
      />
      {/* Top Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                alt="富缘佳旺"
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="text-sm font-bold text-red-700" style={{ fontFamily: "'Noto Serif SC', serif" }}>富缘佳旺</span>
            </Link>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-1.5 text-gray-700">
              <i className="ri-store-2-line text-rose-500"></i>
              <span className="text-sm font-semibold">本地仓储直邮</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {supplier ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-400 rounded-full">
                    <i className="ri-store-2-line text-white text-xs"></i>
                  </div>
                  <span className="text-xs font-semibold text-amber-700">{supplier.name}</span>
                  <span className="text-xs text-amber-500">{supplier.city}</span>
                </div>
                <button
                  onClick={() => setShowUpload(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white rounded-full text-sm font-semibold hover:bg-rose-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-upload-cloud-line"></i> 上传商品
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-1.5 px-5 py-2 bg-gradient-to-r from-rose-600 to-amber-500 text-white rounded-full text-sm font-semibold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-store-2-line"></i> 供应商登录 / 注册
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-700 via-amber-500 to-yellow-400 py-16 md:py-20">
        <img
          src="https://public.readdy.ai/ai/img_res/7754c659e87eddc5cc5b5917ff4b1726.jpg"
          alt="warehouse hero"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-amber-800/70 via-amber-600/50 to-yellow-600/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-5 py-2 mb-5">
            <i className="ri-global-line text-yellow-200"></i>
            <span className="text-sm text-white/90 font-medium">全球600大城市 · 本地仓储直邮</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            本地仓储 · 直邮到家
          </h1>
          <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-8">
            精选优质服装、婴儿用品、书籍文具，供应商本地发货，快速送达。
            <br />平台仅收取 <strong className="text-yellow-200">2% 佣金</strong>，供应商须持有真实仓储商品方可入驻。
          </p>

          {/* City Badges */}
          <div className="flex flex-wrap justify-center gap-2">
            {warehouseCities.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-xs text-white/70">
                <span>{c.flag}</span><span>{c.city}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Upload Success Toast */}
      {uploadSuccess && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium">
          <i className="ri-checkbox-circle-line"></i> 商品发布成功！审核后将显示在列表中
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Supplier CTA Banner */}
        {!supplier && (
          <div className="bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200 rounded-2xl p-5 md:p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-2xl flex-shrink-0">
                <i className="ri-store-2-line text-amber-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-base">成为本地供应商</h3>
                <p className="text-sm text-gray-500 mt-0.5">平台佣金仅 2%，须持有真实仓储商品，买卖双方下单后直接互换微信/邮箱联系</p>
              </div>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <button
                onClick={() => setShowAuth(true)}
                className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-semibold hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line mr-1.5"></i>免费注册供应商
              </button>
              <button
                onClick={() => setShowAuth(true)}
                className="px-6 py-2.5 border border-amber-400 text-amber-600 rounded-full text-sm font-semibold hover:bg-amber-50 transition-all cursor-pointer whitespace-nowrap"
              >
                已有账号登录
              </button>
            </div>
          </div>
        )}

        {/* Search + Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="搜索商品名称..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-rose-400 transition-colors bg-white"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {warehouseCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat.key ? 'bg-rose-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-rose-300'}`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* City Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveCity('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeCity === 'all' ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-400'}`}
          >
            全部城市
          </button>
          {warehouseCities.map((c) => (
            <button
              key={c.key}
              onClick={() => setActiveCity(c.label)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeCity === c.label ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-400'}`}
            >
              {c.flag} {c.city}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-gray-500">
            共找到 <strong className="text-gray-800">{filtered.length}</strong> 件商品
          </p>
          {supplier && (
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white rounded-full text-sm font-semibold hover:bg-rose-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> 上传新商品
            </button>
          )}
        </div>

        {/* Product Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <i className="ri-store-2-line text-5xl mb-4 block"></i>
            <p className="text-base font-medium">暂无符合条件的商品</p>
            <p className="text-sm mt-1">请尝试其他筛选条件</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {filtered.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 group"
              >
                {/* Image */}
                <div className="relative w-full h-52 overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.badge && (
                    <div className="absolute top-2 left-2 bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 text-xs text-gray-600 flex items-center gap-1">
                    <span>{product.supplierFlag}</span>
                    <span>{product.warehouseCity}</span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="text-xs text-gray-400 mb-1">{product.categoryLabel}</div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-0.5 leading-snug">{product.name}</h3>
                  <p className="text-xs text-gray-400 mb-2">{product.nameEn}</p>

                  {/* Supplier */}
                  <div className="flex items-center gap-1.5 mb-2">
                    <i className="ri-store-2-line text-xs text-amber-500"></i>
                    <span className="text-xs text-gray-500">{product.supplierName} · {product.supplierCity}</span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-3">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className={`ri-star-fill text-xs ${s <= Math.round(product.rating) ? 'text-amber-400' : 'text-gray-200'}`}></i>
                      ))}
                    </div>
                    <span className="text-xs text-gray-400">{product.rating}</span>
                    <span className="text-xs text-gray-300 ml-1">已售{product.sold}</span>
                  </div>

                  {/* Shipping */}
                  <div className="flex items-center gap-1 mb-3">
                    <i className="ri-truck-line text-xs text-green-500"></i>
                    <span className="text-xs text-green-600">{product.shippingDays}发货</span>
                  </div>

                  {/* Price + Order */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-bold text-rose-600">{product.currency}{product.price}</span>
                      {product.originalPrice && (
                        <span className="text-xs text-gray-300 line-through ml-1">{product.currency}{product.originalPrice}</span>
                      )}
                    </div>
                    <button
                      onClick={() => setOrderProduct(product)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-rose-600 text-white rounded-full text-xs font-semibold hover:bg-rose-700 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-shopping-bag-line"></i> 下单
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* How It Works */}
        <div className="mt-16 bg-white rounded-3xl p-8 md:p-10 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            如何运作
          </h2>
          <p className="text-center text-gray-400 text-sm mb-10">简单三步，买卖双方直接联系</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: 'ri-store-2-line',
                title: '供应商注册上传',
                desc: '供应商通过邮箱或手机号免费注册，上传商品图片、价格、库存，填写微信/邮箱联系方式',
                color: 'bg-amber-50 text-amber-600',
              },
              {
                step: '02',
                icon: 'ri-shopping-bag-line',
                title: '买家浏览下单',
                desc: '本地买家按城市、分类筛选商品，填写收货信息和自己的联系方式，一键下单',
                color: 'bg-rose-50 text-rose-600',
              },

            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className={`w-16 h-16 flex items-center justify-center ${item.color} rounded-2xl mx-auto mb-4`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <div className="text-xs font-bold text-gray-300 mb-2">STEP {item.step}</div>
                <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Supplier CTA Bottom */}
        <div className="mt-10 bg-gradient-to-r from-rose-600 to-amber-500 rounded-3xl p-8 md:p-10 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            成为本地供应商，开始销售
          </h2>
          <p className="text-white/70 text-sm mb-6 max-w-lg mx-auto">
            平台佣金仅 2%，须持有真实仓储商品方可入驻。覆盖全球800大城市，包括上海、北京、香港、新加坡、悉尼、迪拜、伦敦、纽约等。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowAuth(true)}
              className="px-8 py-3 bg-white text-rose-600 rounded-full font-bold text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line mr-2"></i>免费注册供应商
            </button>
            <button
              onClick={() => setShowAuth(true)}
              className="px-8 py-3 border border-white/40 text-white rounded-full font-semibold text-sm hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
            >
              已有账号 · 登录上传
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAuth && (
        <SupplierAuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={handleSupplierSuccess}
        />
      )}
      {showUpload && supplier && (
        <ProductUploadModal
          supplier={supplier}
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
      {orderProduct && (
        <OrderModal
          product={orderProduct}
          onClose={() => setOrderProduct(null)}
        />
      )}
    </div>
  );
}
